import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { WaveformBars } from '../../atoms/waveform-bars/waveform-bars';

/**
 * VoiceNotePlayer — Compact audio player with animated waveform bars ("las olas").
 *
 * Supports real audio playback via `audioUrl` or mock/simulated mode when no URL is provided.
 * Waveform bars animate with a bell-curve height distribution and staggered delays.
 *
 * @example
 * <!-- Real audio -->
 * <om-voice-note-player [audioUrl]="noteUrl" [authorName]="'Papá'" [duration]="'0:42'" />
 *
 * @example
 * <!-- Mock mode (no URL) -->
 * <om-voice-note-player [authorName]="'Mamá'" [duration]="'1:05'" />
 */
@Component({
  selector: 'om-voice-note-player',
  imports: [WaveformBars],
  templateUrl: './voice-note-player.html',
  styleUrl: './voice-note-player.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VoiceNotePlayer implements OnInit {
  /** Remote audio URL. If null, playback is simulated (mock mode). */
  audioUrl = input<string | null>(null);
  /** Author name shown below waveform. */
  authorName = input<string>('');
  /** Fallback display duration when no real audio is loaded (e.g. "0:42"). */
  duration = input<string>('0:00');
  /** Number of waveform bars. Default 20. Use higher values for wider containers. */
  barCount = input<number>(20);

  private readonly destroyRef = inject(DestroyRef);
  private audio: HTMLAudioElement | null = null;
  private simulatedInterval: ReturnType<typeof setInterval> | null = null;

  readonly state = signal<'idle' | 'playing' | 'paused'>('idle');
  readonly currentSeconds = signal(0);
  readonly totalSeconds = signal(0);

  readonly isPlaying = computed(() => this.state() === 'playing');

  readonly formattedCurrent = computed(() => this.formatTime(this.currentSeconds()));
  readonly formattedTotal = computed(() => {
    if (this.totalSeconds() > 0) return this.formatTime(this.totalSeconds());
    return this.duration();
  });

  ngOnInit(): void {
    const url = this.audioUrl();
    if (url) {
      this.audio = new Audio(url);
      this.audio.onloadedmetadata = () => {
        if (this.audio && isFinite(this.audio.duration)) {
          this.totalSeconds.set(this.audio.duration);
        }
      };
      this.audio.ontimeupdate = () => {
        if (this.audio) this.currentSeconds.set(this.audio.currentTime);
      };
      this.audio.onended = () => {
        this.state.set('idle');
        this.currentSeconds.set(0);
      };
    }
    this.destroyRef.onDestroy(() => this.cleanup());
  }

  toggle(): void {
    if (this.audio) {
      if (this.isPlaying()) {
        this.audio.pause();
        this.state.set('paused');
      } else {
        this.audio.play().then(() => {
          this.state.set('playing');
        }).catch(() => {
          this.state.set('idle');
        });
      }
    } else {
      // Mock mode: simulate playback with a counter
      if (this.isPlaying()) {
        this.stopSimulation();
        this.state.set('paused');
      } else {
        this.state.set('playing');
        this.simulatedInterval = setInterval(() => {
          this.currentSeconds.update(s => s + 1);
        }, 1000);
      }
    }
  }

  private stopSimulation(): void {
    if (this.simulatedInterval) {
      clearInterval(this.simulatedInterval);
      this.simulatedInterval = null;
    }
  }

  private cleanup(): void {
    this.stopSimulation();
    if (this.audio) {
      this.audio.pause();
      this.audio = null;
    }
  }

  private formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }
}
